/**
 * "Where were you going?", answered safely — one rule, four askers.
 *
 * The portal hands a path around in a query parameter three times: the proxy
 * writes `?next=` when it turns somebody away, the sign-in form reads it back
 * afterwards, and the Google handshake carries it out to Google and home
 * again. Each of those used to decide for itself what a path was, and the
 * weakest of them is the one that counts.
 *
 * What a browser will do with `new URL(next, origin)` is the thing being
 * guarded, and it is stranger than it looks:
 *
 *   `//evil.test`    — protocol-relative: another site.
 *   `/\evil.test`    — a backslash is a slash under a special scheme, so
 *                      this is the one above wearing a hat.
 *   `/<TAB>/evil.test` — the URL parser *removes* ASCII tab, CR and LF
 *                      before it parses anything. The tab vanishes and what
 *                      is left is `//evil.test` again. `%09`, `%0a`, `%0d`
 *                      in a query string arrive as those characters.
 *
 * So the rule is stated as what is allowed rather than as a list of what is
 * not: one leading slash, no second one, and nothing below U+0021 or above
 * U+007E anywhere in it. The length cap is here because this value also
 * rides inside the Google handshake cookie, and a cookie over 4096 bytes is
 * dropped by the browser without a word — a sign-in nobody can finish.
 */

/** As long as a real deep link needs, and far short of a cookie's limit. */
export const MAX_NEXT = 512;

// Printable ASCII, minus the backslash: `!` to `[`, then `]` to `~`. Space is
// not in it either — a space in a path is `%20`, and a raw one arriving here
// means somebody is building a string rather than a path.
const SAFE = /^\/(?!\/)[!-\[\]-~]*$/;

/** The path, or null when it is not one. Null rather than a default, so each
 *  caller says what its own default is — "/" for the form, "" for the proxy's
 *  query string, and no `state` suffix at all for the handshake. */
export function safePath(next: string | null | undefined): string | null {
  if (typeof next !== "string" || next.length === 0 || next.length > MAX_NEXT) return null;
  return SAFE.test(next) ? next : null;
}
